/**
 * Campaign Form Component
 * Form for creating and editing campaigns
 */

import React, { useState, useEffect } from "react";
import type {
  Campaign,
  CampaignType,
  CreateCampaignDTO,
  UpdateCampaignDTO,
} from "../../types/campaign.types";
import { OPERATOR_TOGGLEABLE_CAMPAIGN_TYPES } from "../../types/campaign.types";
import { CAMPAIGN_STATUS_OPTIONS as STATUS_OPTIONS } from "../../services/campaigns.service";
import TagAutocompleteInput from "../common/TagAutocompleteInput";

interface Props {
  campaign?: Campaign | null;
  onSubmit: (data: CreateCampaignDTO | UpdateCampaignDTO) => void;
  onCancel: () => void;
  loading?: boolean;
}

const CampaignForm: React.FC<Props> = ({
  campaign,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    status: "draft" as string,
    // 2026-05-27: editable type. Defaults to single_send on create.
    // On edit, hydrated from the existing row in the useEffect below.
    campaign_type: "single_send" as CampaignType,
    // SendGrid reporting category applied to every flight in this campaign (migration
    // 2026-09-16-023). Merged with the IP pool's own category at send; reporting only — it
    // cannot influence which IPs the mail leaves from.
    sendgrid_category: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [applyTag, setApplyTag] = useState("");
  const [removeTag, setRemoveTag] = useState("");

  // Load campaign data if editing
  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name,
        description: campaign.description || "",
        start_date: campaign.start_date
          ? campaign.start_date.split("T")[0]
          : "",
        end_date: campaign.end_date ? campaign.end_date.split("T")[0] : "",
        status: campaign.status || "draft",
        campaign_type: campaign.campaign_type || "single_send",
        sendgrid_category: campaign.sendgrid_category || "",
      });
      setApplyTag((campaign.metadata?.apply_tag as string) || "");
      setRemoveTag((campaign.metadata?.remove_tag as string) || "");
    }
  }, [campaign]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    // Mirrors campaigns_sendgrid_category_format exactly. Validating here is about the MESSAGE,
    // not the enforcement: the CHECK is authoritative, but a raw constraint violation tells the
    // operator nothing about what shape is wanted. A stray capital or space silently splits a
    // month's reporting into two categories, which is why the rule is strict rather than lenient.
    const cat = formData.sendgrid_category.trim();
    if (cat && !/^[a-z0-9][a-z0-9._-]{1,63}$/.test(cat)) {
      newErrors.sendgrid_category =
        "Lowercase letters, numbers, dots, hyphens and underscores only; 2-64 characters; must start with a letter or number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const tagMetadata = {
      ...(applyTag.trim() ? { apply_tag: applyTag.trim() } : {}),
      ...(removeTag.trim() ? { remove_tag: removeTag.trim() } : {}),
    };

    // System-controlled event types stay locked. Operators flip
    // between journey / single_send on the form below; everything
    // else (legacy 'marketing', plus the two event_* types) is sent
    // as-is so we don't accidentally re-categorize event campaigns.
    const isOperatorToggleable = OPERATOR_TOGGLEABLE_CAMPAIGN_TYPES.includes(
      formData.campaign_type,
    );

    if (campaign) {
      const submitData: UpdateCampaignDTO = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        status: formData.status as UpdateCampaignDTO["status"],
        metadata: Object.keys(tagMetadata).length > 0 ? tagMetadata : undefined,
        // Only emit campaign_type when the operator actually chose
        // one (Journey or Single Send). Leaves event_ticket /
        // event_reminder untouched on submit.
        ...(isOperatorToggleable
          ? { campaign_type: formData.campaign_type }
          : {}),
        // null, not undefined: emptying the box must CLEAR the column, and an undefined key
        // would be dropped from the payload and silently leave the old value in place.
        sendgrid_category: formData.sendgrid_category.trim() || null,
      };
      onSubmit(submitData);
    } else {
      const submitData: CreateCampaignDTO = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        campaign_type: formData.campaign_type,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        metadata: Object.keys(tagMetadata).length > 0 ? tagMetadata : undefined,
        ...(formData.sendgrid_category.trim()
          ? { sendgrid_category: formData.sendgrid_category.trim() }
          : {}),
      };
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Campaign Name */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Campaign Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            errors.name ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Spring 2025 Membership Drive"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Brief description of campaign goals and target audience"
        />
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Start Date */}
        <div>
          <label
            htmlFor="start_date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Start Date
          </label>
          <input
            type="date"
            id="start_date"
            value={formData.start_date}
            onChange={(e) => handleChange("start_date", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* End Date */}
        <div>
          <label
            htmlFor="end_date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            End Date
          </label>
          <input
            type="date"
            id="end_date"
            value={formData.end_date}
            onChange={(e) => handleChange("end_date", e.target.value)}
            min={formData.start_date || undefined}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.end_date ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.end_date && (
            <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
          )}
        </div>
      </div>

      {/* Status (edit mode only) */}
      {campaign && (
        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Status
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Series Type. 2026-05-27: editable for marketing / journey /
          single_send; read-only for event_ticket and event_reminder
          which are system-controlled by the events flow. */}
      {(() => {
        const t = formData.campaign_type;
        const isEventType = t === "event_ticket" || t === "event_reminder";
        if (isEventType) {
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Type
              </label>
              <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm">
                {t === "event_ticket" ? "Event Tickets" : "Event Reminder"}
                <span className="ml-2 text-xs text-gray-400">
                  (system-controlled; not operator-toggleable)
                </span>
              </div>
            </div>
          );
        }
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Series Type
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Pick Journey for multi-step drip series (multiple flights spread
              over time). Pick Single Send for one-off blasts. Independent of
              Draft/Active status.
            </p>
            <div className="flex gap-3">
              <label
                className={`flex-1 cursor-pointer rounded-lg border-2 p-3 text-sm transition ${
                  t === "journey"
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="campaign_type"
                  value="journey"
                  checked={t === "journey"}
                  onChange={() => handleChange("campaign_type", "journey")}
                  className="sr-only"
                />
                <div className="font-medium text-gray-900">Journey</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Multi-step drip series spread over time
                </div>
              </label>
              <label
                className={`flex-1 cursor-pointer rounded-lg border-2 p-3 text-sm transition ${
                  t === "single_send"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="campaign_type"
                  value="single_send"
                  checked={t === "single_send"}
                  onChange={() => handleChange("campaign_type", "single_send")}
                  className="sr-only"
                />
                <div className="font-medium text-gray-900">Single Send</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  One-off email blast
                </div>
              </label>
            </div>
            {t === "marketing" && (
              <p className="mt-2 text-xs text-amber-700">
                Legacy "marketing" type — pick Journey or Single Send to
                categorize this campaign.
              </p>
            )}
          </div>
        );
      })()}

      {/* SendGrid reporting category */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-900">
          SendGrid reporting category
        </h3>
        <p className="text-xs text-gray-500 -mt-2">
          Applied to every flight in this campaign, alongside the delivery
          pool's own category. Reporting only &mdash; it does not change which
          IPs the mail sends from.
        </p>
        <div>
          <input
            id="campaign-sendgrid-category"
            type="text"
            value={formData.sendgrid_category}
            onChange={(e) => handleChange("sendgrid_category", e.target.value)}
            placeholder="e.g. 2a-email-09-2026"
            className={`w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.sendgrid_category ? "border-red-400" : "border-gray-300"
            }`}
          />
          {errors.sendgrid_category ? (
            <p className="mt-1 text-sm text-red-600">
              {errors.sendgrid_category}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">
              Lowercase; letters, numbers, dots, hyphens, underscores. Leave
              empty for none. Each flight shows the categories it will actually
              send with.
            </p>
          )}
        </div>
      </div>

      {/* Tag Actions */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-900">Tag Actions</h3>
        <p className="text-xs text-gray-500 -mt-2">
          Apply or remove a tag on contacts when this campaign's flights are
          sent.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <TagAutocompleteInput
            id="campaign-apply-tag"
            label="Apply tag on send"
            value={applyTag}
            onChange={setApplyTag}
            placeholder="e.g. received_campaign"
            helpText="Added to each contact when a flight is sent"
          />
          <TagAutocompleteInput
            id="campaign-remove-tag"
            label="Remove tag on send"
            value={removeTag}
            onChange={setRemoveTag}
            placeholder="e.g. pending_outreach"
            helpText="Removed from each contact when a flight is sent"
          />
        </div>
      </div>

      {/* Helper Text */}
      {!campaign && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            After creating the campaign, you'll be able to add flights
            (email/SMS sends) with specific lists and voucher templates.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          {campaign ? "Update Campaign" : "Create Campaign"}
        </button>
      </div>
    </form>
  );
};

export default CampaignForm;
